import * as React from "react";
import MomentUtils from "@date-io/moment";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link as MUILink,
  TextField,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import {
  MUIDateTimePicker,
  MUIPhoneInput,
  MUIPhoneInputResult,
  SnackbarNotifier,
} from "../panel/MUI";
import { tl } from "../../utils";
import * as styles from "./styles.scss";
import { IAPIMessage, IRegistrationConfig } from "./types";
import { LoginPageContext } from "./context";

interface IRegistrationData {
  name: string;
  surname: string;
  email: string;
  phone: MUIPhoneInputResult;
  city: string;
  company: string;
  position: string;
  messenger: string;
  birthday: string;
  middle: string;
  password: string;
  password2: string;
  accept: boolean;
}

export const RegisterForm = ({
  match,
  registrationConfig,
}: {
  match: any;
  registrationConfig: IRegistrationConfig;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"), {
    noSsr: true
  });
  const { mobileStep, setMobileStep } = React.useContext(LoginPageContext);

  const [hasBeenSubmitted, setHasBeenSubmitted] = React.useState<boolean>(
    false
  );
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const [registrationData, setRegistrationData] = React.useState<
    IRegistrationData
  >({
    name: "",
    surname: "",
    email: "",
    phone: { value: "", isValid: false, countryCode: 'ru' },
    city: "",
    company: "",
    position: "",
    messenger: "",
    birthday: "",
    middle: "",
    password: "",
    password2: "",
    accept: false,
  });
  const [message, setMessage] = React.useState<IAPIMessage>(null);
  const [wrongFields, setWrongFields] = React.useState<string[]>([]);
  const [showPasswordsError, setShowPasswordsError] = React.useState<boolean>(
    false
  );

  const showError = React.useCallback((value: string) => {
    setMessage({
      message: <div dangerouslySetInnerHTML={{__html:value}} />,
      messageType: "error",
    });
  }, []);

  const hideError = React.useCallback(() => {
    setMessage(null);
  }, []);

  const isEmailValid = React.useMemo(() => {
    return (
      registrationData.email &&
      registrationData.email.match(/(.+)@(.+)([\..+]?)/g)
    );
  }, [registrationData.email]);

  const validate = () => {
    setDisabled(true);
    setShowPasswordsError(false);

    if (!isMobile || mobileStep === 0) {
      if (
        !registrationData.name.trim() ||
        !registrationData.email ||
        (registrationConfig.fields.phone && !registrationData.phone.value)
      ) {
        showError(tl("Пожалуйста, заполните выделенные поля"));
        return false;
      }
      if (registrationConfig.fields.phone && !registrationData.phone.isValid) {
        showError(tl("Пожалуйста, укажите корректный номер телефона"));
        return false;
      }
      if (isMobile) {
        hideError();
      }
    }

    if (!isMobile || mobileStep === 1) {
      if (
        !registrationData.password ||
        !registrationData.password2 ||
        (registrationConfig.fields.phone && !registrationData.phone.value) ||
        (registrationConfig.fields.messenger && !registrationData.messenger) ||
        (registrationConfig.fields.city && !registrationData.city) ||
        (registrationConfig.fields.company && !registrationData.company) ||
        (registrationConfig.fields.position && !registrationData.position) ||
        (registrationConfig.fields.middle && !registrationData.middle) ||
        (registrationConfig.fields.birthday && !registrationData.birthday)
      ) {
        showError(tl("Пожалуйста, заполните выделенные поля"));
        return false;
      }
      if (!isEmailValid) {
        showError(tl("Пожалуйста, укажите корректный e-mail"));
        return false;
      }
      setShowPasswordsError(true);
      if (registrationData.password !== registrationData.password2) {
        showError(tl("Введенные пароли не совпадают"));
        return false;
      }
      if (!!registrationConfig.politics && !registrationData.accept) {
        showError(tl("Необходимо согласие на обработку персональных данных"));
        return false;
      }
    }

    setDisabled(false);
    hideError();
    return true;
  };

  React.useEffect(() => {
    if (hasBeenSubmitted) {
      validate();
    }
  }, [registrationData, hasBeenSubmitted]);

  const onNextStep = async (evt: React.FormEvent) => {
    evt.preventDefault();
    evt.stopPropagation();

    setHasBeenSubmitted(true);

    if (validate()) {
      if (!isMobile || mobileStep === 1) {
        setDisabled(true);
        setWrongFields([]);

        const data = new FormData();
        data.append("subtype", Math.random().toString());
        data.append("locale", registrationConfig.locale);
        data.append('country_code', registrationData.phone.countryCode);
        data.append("tags", registrationConfig.tags);
        if (registrationConfig.benefit_id) {
          data.append("benefit_id", registrationConfig.benefit_id);
        }
        if (registrationConfig.course_id)
          data.append("course_id", registrationConfig.course_id);
        if (registrationConfig.group_id)
          data.append("group_id", registrationConfig.group_id);
        data.append("redirect_uri", registrationConfig.redirect);
        data.append("item[name]", registrationData.name);
        data.append("item[surname]", registrationData.surname);
        data.append("item[email]", registrationData.email);
        if (registrationConfig.fields.phone) {
          data.append("item[phone]", registrationData.phone.value);
        }
        if (registrationConfig.fields.messenger) {
          data.append("item[messenger]", registrationData.messenger);
        }
        if (registrationConfig.fields.city) {
          data.append("item[city]", registrationData.city);
        }
        if (registrationConfig.fields.company) {
          data.append("item[company]", registrationData.company);
        }
        if (registrationConfig.fields.position) {
          data.append("item[position]", registrationData.position);
        }
        if (registrationConfig.fields.birthday) {
          data.append("item[birthday]", registrationData.birthday);
        }
        if (registrationConfig.fields.middle) {
          data.append("item[middle]", registrationData.middle);
        }
        data.append("item[password]", registrationData.password);
        data.append("item[password_repeat]", registrationData.password2);
        data.append("accept", "yes");

        const response = await fetch(`/account/register`, {
          method: "post",
          credentials: "include",
          body: data,
        });
        const result = await response.json();

        if (result.status === "ok" && result.action === "redirect") {
          (window as any).location.href = result.url;
        } else {
          if (isMobile) {
            setMobileStep(0);
          }
          if (result.type === "field") {
            setWrongFields(result.subject);
          }
          showError(
            result.message ||
              result.content ||
              tl("Произошла ошибка, попробуйте повторить позднее")
          );
        }

        setDisabled(false);
      } else {
        setHasBeenSubmitted(false);
        setMobileStep(mobileStep + 1);
      }
    }
  };

  return (
    <div className={styles.wrap}>
      <form onSubmit={onNextStep} noValidate>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Box className={styles.title}>{tl("Впервые у нас")}?</Box>
          </Grid>
          {message ? (
            <Grid item>
              <SnackbarNotifier inline note={message} className="nominwidth" />
            </Grid>
          ) : null}
          <Grid item container spacing={2}>
            <Grid
              item
              xs={12}
              sm={6}
              className={mobileStep !== 0 ? styles.xsHidden : ""}
            >
              <TextField
                data-testid="name"
                variant="outlined"
                color="primary"
                error={
                  hasBeenSubmitted &&
                  (!registrationData.name.trim() || wrongFields.includes("item[name]"))
                }
                fullWidth
                placeholder={tl("Имя")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[name]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    name: evt.target.value,
                  });
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              className={mobileStep !== 0 ? styles.xsHidden : ""}
            >
              <TextField
                data-testid="surname"
                variant="outlined"
                color="primary"
                error={wrongFields.includes("item[surname]")}
                fullWidth
                placeholder={tl("Фамилия")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[surname]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    surname: evt.target.value,
                  });
                }}
              />
            </Grid>
          </Grid>
          {registrationConfig.fields.middle ? (
              <Grid
                  item
                  xs={12}
                  className={mobileStep !== 1 ? styles.xsHidden : ""}
              >
                <TextField
                    variant="outlined"
                    color="primary"
                    fullWidth
                    error={
                        hasBeenSubmitted &&
                        (!registrationData.middle || wrongFields.includes("item[middle]"))
                    }
                    type="text"
                    placeholder={tl("Отчество")}
                    onChange={(evt) => {
                      setWrongFields(
                          wrongFields.filter((id) => id !== "item[middle]")
                      );
                      setRegistrationData({
                        ...registrationData,
                        middle: evt.target.value,
                      });
                    }}
                    required
                />
              </Grid>
          ) : null}
          <Grid
            item
            xs={12}
            className={mobileStep !== 0 ? styles.xsHidden : ""}
          >
            <TextField
              variant="outlined"
              color="primary"
              fullWidth
              error={
                hasBeenSubmitted &&
                (!registrationData.email ||
                  !isEmailValid ||
                  wrongFields.includes("item[email]"))
              }
              type="email"
              placeholder={tl("E-mail адрес")}
              onChange={(evt) => {
                setWrongFields(
                  wrongFields.filter((id) => id !== "item[email]")
                );
                setRegistrationData({
                  ...registrationData,
                  email: evt.target.value,
                });
              }}
              required
            />
          </Grid>
          {registrationConfig.fields.phone ? (
            <Grid
              item
              xs={12}
              className={mobileStep !== 0 ? styles.xsHidden : ""}
            >
              <MUIPhoneInput
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.phone.value ||
                    !registrationData.phone.isValid ||
                    wrongFields.includes("item[phone]"))
                }
                onChange={(phoneData) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[phone]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    phone: phoneData,
                  });
                }}
              />
            </Grid>
          ) : null}
          <TextField
            style={{ width: 0, height: 0 }}
            InputProps={{ inputProps: { tabIndex: -1 } }}
          />
          {registrationConfig.fields.city ? (
            <Grid
              item
              xs={12}
              className={mobileStep !== 1 ? styles.xsHidden : ""}
            >
              <TextField
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.city || wrongFields.includes("item[city]"))
                }
                type="text"
                placeholder={tl("Город проживания")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[city]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    city: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
          ) : null}
          {registrationConfig.fields.messenger ? (
            <Grid
              item
              xs={12}
              className={mobileStep !== 1 ? styles.xsHidden : ""}
            >
              <TextField
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.messenger ||
                    wrongFields.includes("item[messenger]"))
                }
                type="text"
                placeholder={tl("Мессенджер для связи")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[messenger]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    messenger: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
          ) : null}
          {registrationConfig.fields.company ? (
            <Grid
              item
              xs={12}
              className={mobileStep !== 1 ? styles.xsHidden : ""}
            >
              <TextField
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.company ||
                    wrongFields.includes("item[company]"))
                }
                type="text"
                placeholder={tl("Название организации")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[company]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    company: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
          ) : null}
          {registrationConfig.fields.position ? (
            <Grid
              item
              xs={12}
              className={mobileStep !== 1 ? styles.xsHidden : ""}
            >
              <TextField
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.position ||
                    wrongFields.includes("item[position]"))
                }
                type="text"
                placeholder={tl("Ваша должность")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[position]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    position: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
          ) : null}
          {registrationConfig.fields.birthday ? (
              <Grid
                  item
                  xs={12}
                  className={mobileStep !== 1 ? styles.xsHidden : ""}
              >
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <MUIDateTimePicker
                    fullWidth
                    dateOnly
                    placeholder={tl("Дата рождения")}
                    value={registrationData.birthday}
                    autoComplete="one-time-code"
                    isError={
                      hasBeenSubmitted &&
                      (!registrationData.birthday ||
                          wrongFields.includes("item[birthday]"))
                    }
                    onChange={(value) => {
                      setWrongFields(
                        wrongFields.filter((id) => id !== "item[birthday]")
                      );
                      setRegistrationData({
                        ...registrationData,
                        birthday: value,
                      });
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
          ) : null}
          <Grid
            item
            container
            spacing={2}
            className={mobileStep !== 1 ? styles.xsHidden : ""}
          >
            <Grid item xs={12} sm={6}>
              <TextField
                data-testid="password"
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.password ||
                    (showPasswordsError &&
                      !!registrationData.password &&
                      !!registrationData.password2 &&
                      registrationData.password !==
                        registrationData.password2) ||
                    wrongFields.includes("item[password]"))
                }
                type="password"
                placeholder={tl("Пароль")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[password]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    password: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                data-testid="repeatPassword"
                variant="outlined"
                color="primary"
                fullWidth
                error={
                  hasBeenSubmitted &&
                  (!registrationData.password2 ||
                    (showPasswordsError &&
                      !!registrationData.password &&
                      !!registrationData.password2 &&
                      registrationData.password !==
                        registrationData.password2) ||
                    wrongFields.includes("item[password_repeat]"))
                }
                type="password"
                placeholder={tl("Повторите пароль")}
                onChange={(evt) => {
                  setWrongFields(
                    wrongFields.filter((id) => id !== "item[password_repeat]")
                  );
                  setRegistrationData({
                    ...registrationData,
                    password2: evt.target.value,
                  });
                }}
                required
              />
            </Grid>
          </Grid>
          {registrationConfig.politics ? (
            <Grid item className={mobileStep !== 1 ? styles.xsHidden : ""}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={registrationData.accept}
                    onChange={(evt: any) => {
                      setWrongFields(
                        wrongFields.filter((id) => id !== "item[accept]")
                      );
                      setRegistrationData({
                        ...registrationData,
                        accept: evt.target.checked,
                      });
                    }}
                    className={
                      hasBeenSubmitted &&
                      (!registrationData.accept ||
                        wrongFields.includes("accept"))
                        ? "MuiCheckbox-error"
                        : ""
                    }
                    required
                  />
                }
                label={
                  <>
                    {tl("Согласен на обработку ")}
                    <MUILink href={registrationConfig.politics}>
                      {tl("персональных данных")}
                    </MUILink>
                  </>
                }
              />
            </Grid>
          ) : null}
          <Grid item>
            <Button
              data-testid="register"
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={disabled}
            >
              {tl("Зарегистрироваться")}
            </Button>
          </Grid>
          {isMobile &&
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                setMobileStep(5);
              }}
            >
              {tl("Уже зарегистрированы?")}
            </Button>
          }
        </Grid>
      </form>
    </div>
  );
};
